import { View, Text, type ViewStyle } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { MoonSkeleton } from "./MoonSkeleton";

/**
 * Wraps a single form input with a consistent label, optional helper text,
 * and optional error message — the shared "field" chrome around any input.
 *
 * Resolves the label-and-helper drift documented in audit task 2.2: every
 * modal in `care/modals/` re-declares its own `FieldLabel` styling, error
 * display is inconsistent, and helper text is mostly missing. This primitive
 * consolidates all three.
 *
 * State coverage matches Phase 1.0's component matrix for form-field
 * chrome:
 *   - idle:     just the label + the child input
 *   - error:    `error` prop truthy → label colour shifts, error text shown
 *   - disabled: `disabled` prop → label dims to textFaint
 *   - loading:  `loading` prop → child is swapped for a MoonSkeleton at the
 *               same height (default 44px). Useful when the form's
 *               server-side data is still fetching.
 *
 * The `focused` state lives on the inner input itself, not on this wrapper.
 *
 * Example:
 *   <MoonFormField
 *     label="Treatment type"
 *     hint="We tailor your plan from this answer"
 *     error={errors.treatmentType?.message}
 *     required
 *   >
 *     <Select ... />
 *   </MoonFormField>
 */
type Props = {
  /** Optional label rendered above the input, preserving the supplied casing. */
  label?: string;
  /** Decorative Lucide icon rendered immediately before the label. */
  labelIcon?: LucideIcon;
  /**
   * Short supporting copy rendered directly below the label and before the
   * input. Use this for context that describes the field itself.
   */
  labelHint?: string;
  /**
   * The input element this field wraps. Optional only because `loading`
   * swaps the child for a skeleton at render time — every non-loading
   * caller should pass children.
   */
  children?: React.ReactNode;
  /**
   * Error string. When truthy, the label colour shifts to `coral` and this
   * string is rendered below the input in coral. Takes precedence over `hint`.
   */
  error?: string;
  /**
   * Helper text shown below the input when there's no error. Use for short
   * "this is how we use it" copy; not for validation rules (those belong
   * inside the input's own placeholder/format).
   */
  hint?: string;
  /** Adds a coral dot after the label to signal a required field. */
  required?: boolean;
  /** Dims the label and lets the input show its own disabled treatment. */
  disabled?: boolean;
  /** Replaces children with a skeleton at the same height. */
  loading?: boolean;
  /**
   * Approximate height of the wrapped input, used for the skeleton placeholder
   * in `loading` state. Defaults to 44 (the standard tap target).
   */
  loadingHeight?: number;
  /** Forwarded to the outer wrapper for snapshot/E2E. */
  testID?: string;
  /** Additional style on the outer wrapper. */
  style?: ViewStyle;
  /** Visual tone for surfaces that sit on the dark onboarding glass. */
  tone?: "default" | "dark";
};

const LABEL_STYLE = {
  fontFamily: typography.families.sans,
  fontSize: 13,
  fontWeight: "400" as const,
  letterSpacing: 0,
};

export function MoonFormField({
  label,
  labelIcon: LabelIcon,
  labelHint,
  children,
  error,
  hint,
  required,
  disabled,
  loading,
  loadingHeight = 44,
  testID,
  style,
  tone = "default",
}: Props) {
  // Match the shared SectionTitle tone without inheriting its uppercase or
  // heavyweight typography. Error shifts to coral; disabled dims via opacity.
  const labelColor = error
    ? colors.coral
    : tone === "dark"
      ? "rgba(255, 255, 255, 0.72)"
      : colors.textLight;

  // Error takes precedence over hint — never show both. An empty helper row
  // collapses (no reserved space) to avoid layout jitter when validation
  // arrives mid-form.
  const helperText = error || hint;
  const helperColor = error
    ? colors.coral
    : tone === "dark"
      ? "rgba(255, 255, 255, 0.62)"
      : colors.textLight;

  return (
    <View testID={testID} style={[disabled ? { opacity: 0.5 } : null, style]}>
      {label ? (
        <View style={{ marginBottom: 6 }}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {LabelIcon ? (
              <LabelIcon
                size={13}
                color={labelColor}
                strokeWidth={1.75}
                style={{ marginRight: 6 }}
              />
            ) : null}
            <Text style={[LABEL_STYLE, { color: labelColor }]}>{label}</Text>
            {required ? (
              <Text
                accessibilityLabel="required"
                style={[LABEL_STYLE, { color: colors.coral, marginLeft: 4 }]}
              >
                *
              </Text>
            ) : null}
          </View>
          {labelHint ? (
            <Text
              style={{
                fontFamily: typography.families.sans,
                fontSize: 12,
                lineHeight: 16,
                color: colors.textFaint,
                marginTop: 2,
              }}
            >
              {labelHint}
            </Text>
          ) : null}
        </View>
      ) : null}

      {loading ? (
        <MoonSkeleton width="100%" height={loadingHeight} radius={10} />
      ) : (
        children
      )}

      {helperText ? (
        <Text
          style={{
            fontFamily: typography.families.sans,
            fontSize: 12,
            color: helperColor,
            marginTop: 6,
            lineHeight: 16,
          }}
        >
          {helperText}
        </Text>
      ) : null}
    </View>
  );
}
